<script setup lang="ts">
import { ref } from 'vue';

const props = withDefaults(defineProps<{
    blur?: boolean,
    dim?: boolean,
}>(), {
    blur: false,
    dim: false,
});

let dialog = ref();
let isClosing = ref(false);
let isOpen = ref(false);

function close(immediate = false) {
    return new Promise<void>((resolve) => {
        if (immediate) {
            dialog.value.close();
            isOpen.value = false;
            resolve();
            emit("closed");
        } else {
            isClosing.value = true;
            setTimeout(() => {
                dialog.value.close();
                isClosing.value = false;
                isOpen.value = false;
                resolve();
                emit("closed");
            }, 150);
        }
    });
}

const emit = defineEmits<{
  (e: 'opened'): void;
  (e: 'closed'): void;
}>();

defineExpose({
    open() {
        isOpen.value = true;
        dialog.value.showModal();
        emit("opened");
    },
    close,
    isOpen,
});
</script>
<template>
    <dialog ref="dialog" @click="close()" :class="{ closing: isClosing, blur, dim }">
        <div class="modal-body" @click.stop>
            <button id="close" @click="close()">
                <v-icon name="fa-times" />
            </button>
            <slot />
        </div>
    </dialog>
</template>

<style scoped>
dialog.blur::backdrop {
    backdrop-filter: blur(5px);
}

dialog.dim::backdrop {
    background: rgba(0, 0, 0, 0.5);
}

dialog {
    opacity: 1;
    transform: scale(1);
    transition: all 0.25s ease-in-out;
    padding: 0;
    background-color: rgba(0, 0, 0, 0);
    border: none;
    z-index: 100;

    @starting-style {
        opacity: 0;
        transform: scale(0.8);
    }
}

dialog.closing {
    transition-duration: .15s;
    opacity: 0;
    transform: scale(0.8);
}

.modal-body {
    position: relative;
}

button#close {
    position: absolute;
    display: grid;
    place-items: center;
    top: 0.5rem;
    right: 0.5rem;
    background-color: transparent;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
}
</style>